import { ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export const confirmPasswordValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const password = control.get('password');
  const confirm_password = control.get('confirm_password');
  return password &&
    confirm_password &&
    password.value === confirm_password.value
    ? null
    : { confirmPasswordInvalid: true };
};

export const emailPhoneValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value?.trim();
  if (!value) return null;

  const isValid = isValidMobileNumber(value) || isValidEmail(value);

  return !isValid ? { emailPhone: true } : null;
};

function isValidMobileNumber(value: string) {
  return /^(?:0|94|0094|\+94)[0-9]{9}$/.test(value);
}

function isValidEmail(value: string) {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
}

export function calculateAge(
  dobValue: Date | string | null | undefined,
): number {
  if (!dobValue) return 0;
  const dob = new Date(dobValue);
  const ageDifMs = Date.now() - dob.getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}

export function isUnderAge(
  dobValue: Date | string | null | undefined,
  ageThreshold = 16,
): boolean {
  if (!dobValue) return false;
  return calculateAge(dobValue) < ageThreshold;
}

export function hasFieldError(
  form: FormGroup,
  fieldName: string,
  errorType: string,
): boolean {
  const field = form.get(fieldName);
  return (
    (field?.invalid &&
      (field?.dirty || field?.touched) &&
      field.hasError(errorType)) ||
    false
  );
}

export function isFieldInvalid(form: FormGroup, fieldName: string): boolean {
  const field = form.get(fieldName);
  return (form?.invalid && (field?.dirty || field?.touched)) || false;
}

export function createGoogleOnboardingForm(
  fb: NonNullableFormBuilder,
  destroyRef: DestroyRef,
  cdr?: ChangeDetectorRef,
): FormGroup {
  const form = fb.group({
    dateOfBirth: ['', Validators.required],
    parentEmail: ['', Validators.email],
  });

  form
    .get('dateOfBirth')
    ?.valueChanges.pipe(takeUntilDestroyed(destroyRef))
    .subscribe((dobValue) => {
      if (dobValue) {
        const under16 = isUnderAge(dobValue, 16);
        const parentEmailControl = form.get('parentEmail');
        if (under16) {
          parentEmailControl?.setValidators([
            Validators.required,
            Validators.email,
          ]);
        } else {
          parentEmailControl?.setValidators([Validators.email]);
        }
        queueMicrotask(() => {
          parentEmailControl?.updateValueAndValidity();
          if (cdr) cdr.markForCheck();
        });
      }
    });

  return form;
}
